import api from "../config/api";
import { getErrorMessage } from "../config/errorHelper";

// ─────────────────────────────────────────────
//  CREDENTIALS
// ─────────────────────────────────────────────

/**
 * Liste toutes les credentials d'une entreprise.
 * Filtres optionnels : keyword, scope, type, folderId.
 */
export const getCredentials = async (companyId, filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.keyword)  params.append("keyword",  filters.keyword);
    if (filters.scope)    params.append("scope",    filters.scope);
    if (filters.type)     params.append("type",     filters.type);
    if (filters.folderId) params.append("folderId", filters.folderId);

    const query = params.toString() ? `?${params}` : "";
    const res = await api.get(`/credentials/admin/company/${companyId}${query}`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Mot de passe déchiffré d'une credential.
 * ⚠️ Ne jamais stocker le résultat hors du state local du composant.
 */
export const getPassword = async (id) => {
  try {
    const res = await api.get(`/credentials/admin/${id}/password`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Crée une credential pour une entreprise.
 * payload : {
 *   name, username?, encryptedPassword (en clair — chiffré par le backend),
 *   url?, host?, port?, notes?, type, scope,
 *   company: { id }, folder?: { id }
 * }
 */
export const createCredential = async (payload) => {
  try {
    const res = await api.post("/credentials/admin/create", payload);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/** Modifie une credential (patch partiel — seuls les champs fournis sont mis à jour). */
export const updateCredential = async (id, payload) => {
  try {
    const res = await api.put(`/credentials/admin/update/${id}`, payload);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/** Supprime une credential. */
export const deleteCredential = async (id) => {
  try {
    const res = await api.delete(`/credentials/admin/delete/${id}`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Génère un mot de passe fort conforme à la politique de l'entreprise.
 * @param {string} companyId
 * @param {number} length    longueur souhaitée (défaut : 16)
 */
export const generatePassword = async (companyId, length = 16) => {
  try {
    const res = await api.get(`/credentials/admin/generate-password/${companyId}?length=${length}`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

// ─────────────────────────────────────────────
//  DOSSIERS
// ─────────────────────────────────────────────

/** Liste les dossiers d'une entreprise (triés par nom). */
export const getFolders = async (companyId) => {
  try {
    const res = await api.get(`/credentials/admin/folders/${companyId}`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/** Crée un dossier. payload : { name, color?, company: { id } } */
export const createFolder = async (payload) => {
  try {
    const res = await api.post("/credentials/admin/folders/create", payload);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/** Renomme ou recolore un dossier. */
export const updateFolder = async (id, payload) => {
  try {
    const res = await api.put(`/credentials/admin/folders/${id}`, payload);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/** Supprime un dossier (les credentials reviennent à la racine). */
export const deleteFolder = async (id) => {
  try {
    const res = await api.delete(`/credentials/admin/folders/${id}`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

// ─────────────────────────────────────────────
//  POLITIQUE DE MOT DE PASSE
// ─────────────────────────────────────────────

/**
 * Récupère la politique de mot de passe d'une entreprise.
 * Retourne null si aucune politique n'est configurée.
 */
export const getPolicy = async (companyId) => {
  try {
    const res = await api.get(`/credentials/admin/policy/${companyId}`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/** Crée ou met à jour la politique de mot de passe d'une entreprise (upsert). */
export const savePolicy = async (companyId, payload) => {
  try {
    const res = await api.put(`/credentials/admin/policy/${companyId}`, payload);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

// ─────────────────────────────────────────────
//  TOTP — Authentification à deux facteurs
//  L'admin peut gérer le 2FA de n'importe quelle credential.
// ─────────────────────────────────────────────

/**
 * Enregistre le secret TOTP d'une credential.
 * @param {string} id     UUID de la credential
 * @param {string} secret Secret TOTP en clair (ex: "JBSWY3DPEHPK3PXP")
 */
export const saveTotpSecret = async (id, secret) => {
  try {
    const res = await api.put(`/credentials/admin/${id}/totp`, { secret });
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/** Supprime le secret TOTP d'une credential (désactive le 2FA). */
export const deleteTotpSecret = async (id) => {
  try {
    const res = await api.delete(`/credentials/admin/${id}/totp`);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Récupère le code TOTP actuel pour une credential.
 * @returns {{ code: string, secondsRemaining: number, period: number }}
 */
export const getTotpCode = async (id) => {
  try {
    const res = await api.get(`/credentials/admin/${id}/totp`);
    return res.data?.data ?? res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

// ─────────────────────────────────────────────
//  Fonctions pour MyPasswordsPage (mots de passe personnels de l'admin)
//  Utilise les endpoints client /credentials/personal
//  (pas les endpoints admin/company)
// ─────────────────────────────────────────────

/** Credentials PERSONNELLES de l'admin connecté */
export const getPersonalCredentials = async () => {
  try {
    const res = await api.get("/credentials/personal");
    return res.data;
  } catch (err) { throw new Error(getErrorMessage(err)); }
};

/** Company credentials — retourne vide dans MyPasswordsPage */
export const getCompanyCredentials = async () => {
  return { data: [] };
};

/** Mot de passe déchiffré d'une credential personnelle */
export const getCredentialPassword = async (id) => {
  try {
    const res = await api.get(`/credentials/${id}/password`);
    return res.data;
  } catch (err) { throw new Error(getErrorMessage(err)); }
};